/* Verified walk-in cart additions. expectedPrice is the unit price in cents. */
(() => {
  'use strict';
  const money = cents => new Intl.NumberFormat('en-US', {style:'currency', currency:'USD'}).format(cents / 100);
  const failure = (message, code, review = false) => Object.assign(new Error(message), {code, cartReviewRequired:review});
  // A stalled request must reach the existing retry-safe or cart-review state.
  // Keep the deadline active through body parsing; never retry an add request.
  async function request(url, options, readJson = false) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(url, {...options, signal:controller.signal});
      const data = readJson && response.ok ? await response.json() : null;
      return {response, data};
    } finally {
      clearTimeout(timeout);
    }
  }
  async function add({items, properties = {}, onStatus} = {}) {
    if (!Array.isArray(items) || !items.length) throw failure('Select a complete configuration before adding to cart.', 'INVALID_ITEMS');
    const combined = new Map();
    for (const item of items) {
      const id = String(item.id), quantity = Number(item.quantity), price = item.expectedPrice;
      if (!/^\d+$/.test(id) || !Number.isSafeInteger(Number(id)) || Number(id) <= 0 || !Number.isSafeInteger(quantity) || quantity < 1 || !Number.isSafeInteger(price) || price < 0) throw failure('This configuration has an invalid item or price. Please review your selections.', 'INVALID_ITEMS');
      const previous = combined.get(id);
      if (previous && previous.expectedPrice !== price) throw failure('A selected item has conflicting prices. Please review your configuration.', 'INVALID_ITEMS');
      const totalQuantity = quantity + (previous?.quantity || 0);
      if (!Number.isSafeInteger(totalQuantity)) throw failure('The requested quantity is too large.', 'INVALID_ITEMS');
      combined.set(id, {id, quantity:totalQuantity, expectedPrice:price, label:item.label || 'Selected option'});
    }
    const status = text => {if (typeof onStatus === 'function') {try {onStatus(text);} catch (_) { /* UI updates must not interrupt cart verification. */ }}};
    const root = new URL(window.Shopify?.routes?.root || '/', window.location.origin);
    if (root.origin !== window.location.origin) throw failure('The cart route could not be verified. Please reload the page.', 'INVALID_ROUTE');
    if (!root.pathname.endsWith('/')) root.pathname += '/';
    const url = path => new URL(path, root).href;
    status('Checking selected items and prices…');
    for (const item of combined.values()) {
      let response, variant;
      try {
        ({response, data:variant} = await request(url(`variants/${item.id}.js`), {credentials:'same-origin', headers:{Accept:'application/json'}}, true));
        if (!response.ok) throw failure(`${item.label || 'Selected option'} could not be verified. Nothing was added. Please request help with this configuration.`, 'ITEM_UNAVAILABLE');
      } catch (error) {
        if (typeof error.code === 'string') throw error;
        throw failure('Selected items could not be checked. Nothing was added. Check your connection and try again.', 'VERIFY_FAILED');
      }
      if (String(variant.id) !== item.id || variant.available !== true || !Number.isSafeInteger(variant.price)) throw failure(`${item.label || 'Selected option'} is unavailable or incomplete. Nothing was added. Please request help with this configuration.`, 'ITEM_UNAVAILABLE');
      if (variant.price !== item.expectedPrice) throw failure(`The current price for ${item.label || 'selected option'} is ${money(variant.price)}, rather than ${money(item.expectedPrice)}. Nothing was added. Please confirm the updated configuration price.`, 'PRICE_CHANGED');
    }
    status('Adding the verified configuration…');
    const review = () => failure('The cart response could not be confirmed. Open your cart and review its items and prices before trying again; this configuration may already have been added.', 'CART_REVIEW_REQUIRED', true);
    // Verify the persisted configuration: storefront apps may transform the
    // add response, so it is not authoritative evidence of the complete cart.
    const configurationId = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    // Preserve the expected component ratios on every line so cart updates can
    // detect a removed component without relying on the remaining line count.
    const gcd = (a, b) => {while (b) {const remainder = a % b; a = b; b = remainder;} return a;};
    const buildQuantity = [...combined.values()].reduce((count, item) => gcd(count, item.quantity), 0);
    const buildManifest = JSON.stringify({version:1, items:[...combined.values()].map(item => ({id:item.id, ratio:item.quantity / buildQuantity, price:item.expectedPrice}))});
    const cartProperties = {...properties, '_AMC configuration':configurationId, '_AMC build':buildManifest};
    let response;
    try {
      ({response} = await request(url('cart/add.js'), {method:'POST', credentials:'same-origin', headers:{'Content-Type':'application/json', Accept:'application/json'}, body:JSON.stringify({items:[...combined.values()].map(({id,quantity}) => ({id:Number(id), quantity, properties:cartProperties}))})}));
    } catch (_) {throw review();}
    if (!response.ok) throw failure('Shopify did not confirm the configuration. Review your cart before trying again.', 'CART_REJECTED', true);
    status('Verifying your complete build in the cart…');
    let cart;
    try {
      const {response:persisted, data} = await request(url('cart.js'), {credentials:'same-origin', cache:'no-store', headers:{Accept:'application/json'}}, true);
      if (!persisted.ok) throw review();
      cart = data;
    } catch (_) {throw review();}
    if (!Array.isArray(cart.items)) throw review();
    const data = {items:cart.items.filter(line => line.properties?.['_AMC configuration'] === configurationId)};
    if (data.items.length !== combined.size) throw review();
    const seen = new Set();
    for (const line of data.items) {
      const id = String(line.variant_id ?? line.id), expected = combined.get(id);
      if (!expected || seen.has(id) || line.quantity !== expected.quantity || line.original_price !== expected.expectedPrice || (line.price !== undefined && line.price !== expected.expectedPrice) || line.properties?.['_AMC build'] !== buildManifest) throw review();
      seen.add(id);
    }
    if (window.AMCNfda) {
      try { await window.AMCNfda.apply(); }
      catch (error) { throw failure(error.message, 'promotion_review', true); }
    }
    status('Configuration added to cart.');
    return data;
  }
  window.AMCWalkinCart = Object.freeze({add});
})();

(() => {
  const STORAGE_KEY = 'amcWalkInConfiguration';
  let liveCatalog = {};
  try {liveCatalog = JSON.parse(document.getElementById('amc-walkin-catalog-data')?.textContent || '{}');} catch (_) {}
  const liveRamp = () => {
    const r = liveCatalog.ramp;
    return r && Number.isSafeInteger(r.id) && r.id > 0 && Number.isSafeInteger(r.price) && r.price > 0 && r.available ? r : null;
  };
  const REQUIRED_OPTION_IDS = new Set(['bLoc', 'bDoor', 'bHinge', 'bRef', 'r']);
  const REQUIRED_PLACEHOLDER_VALUE = '';
  const CONTROL_SELECTOR = 'select:is(#bLoc,#bDoor,#bHinge,#bHandle,#bPlate,#bRef,#bFloor,#bRamp,#bLed,#r,#f,#l)';
  const LABELS = {bLoc:'Door location',bDoor:'Door size',bHinge:'Door hinge',bHandle:'Door handle',bPlate:'Diamond plate',bRef:'Refrigeration',r:'Refrigeration',bFloor:'Insulated floor',f:'Insulated floor',bRamp:'Ramp',bLed:'LED lighting',l:'LED lighting'};
  const CATEGORIES = [
    ['bRef','Refrigeration','r'], ['bLoc','Door location'], ['bDoor','Door size'], ['bHinge','Door hinge'],
    ['bHandle','Door handle'], ['bPlate','Diamond plate'],
    ['bFloor','Insulated floor','f'], ['bRamp','Ramp'], ['bLed','LED lighting','l'],
  ];
  const PREFERENCE_SELECTOR = '[data-amc-quote-preference]';
  function freezerDoorData(builder) {
    if (!builder.dataset.amcFreezerDoors) return null;
    try {return JSON.parse(builder.dataset.amcFreezerDoors);} catch (_) {return null;}
  }
  function quoteOnlySelection(builder) {
    if (builder.dataset.amcFloorPricesPending === 'true' || builder.querySelector('select[data-amc-rule="floor"] option:checked[data-amc-price-unverified]') || (hasRamp(builder) && !liveRamp())) return true;
    return [...builder.querySelectorAll('select')].some(field => {
      if (field.closest('[hidden]') || isPlaceholder(field)) return false;
      return field.selectedOptions[0]?.dataset.amcQuoteRequired === 'true'
        || (field.dataset.amcQuotePreference && field.selectedIndex > 0);
    });
  }
  function prepareFreezerDoors(builder, controls) {
    const source = document.getElementById('amc-freezer-door-data');
    if (!source) return;
    let data;
    try {data = JSON.parse(source.textContent);} catch (_) {return;}
    const dimensions = data.handle.match(/^(\d+)x(\d+)-/);
    if (!dimensions) return;
    builder.dataset.amcFreezerDoors = JSON.stringify(data);
    const add = (id, options) => {
      if (builder.querySelector('#' + id)) return;
      const label = document.createElement('label');
      label.htmlFor = id; label.textContent = LABELS[id];
      const select = document.createElement('select'); select.id = id;
      for (const item of options) {
        const option = new Option(item.label, item.value);
        if (item.price !== undefined) option.dataset.p = String(item.price / 100);
        if (item.disabled) option.disabled = true;
        select.add(option);
      }
      builder.append(label, select); controls.push(select);
    };
    const [width, depth] = dimensions.slice(1);
    const wallNames = width === depth ? ['Front wall', 'Side wall'] : [`${width}-ft wall`, `${depth}-ft wall`];
    add('bLoc', wallNames.flatMap(wall => [
      {label:`Corner of ${wall}`, value:`Corner of ${wall}`},
      {label:`Off-center of ${wall}`, value:`Off-center of ${wall}`},
    ]));
    add('bHinge', [
      {label:'Right-hand swing', value:'Right-hand swing'},
      {label:'Left-hand swing', value:'Left-hand swing'},
    ]);
    const door = data.components.door, handle = data.components.handle;
    add('bDoor', [
      {label:'36-inch standard door — included', value:'standard36', price:0},
      {label:door.available ? `48-inch door upgrade — add ${moneyTextPrice(door.price)}` : '48-inch door upgrade — unavailable',
        value:String(door.id), price:door.price, disabled:!door.available || !door.id},
    ]);
    add('bHandle', [
      {label:'Standard handle — included', value:'', price:0},
      {label:handle.available ? `Lockable handle — add ${moneyTextPrice(handle.price)}` : 'Lockable handle — unavailable',
        value:String(handle.id), price:handle.price, disabled:!handle.available || !handle.id},
    ]);
    add('bPlate', [
      {label:'No diamond plate', value:'no'},
      {label:`Diamond plate — ${moneyTextPrice(data.components.plate36.price)} (36-inch door) / ${moneyTextPrice(data.components.plate48.price)} (48-inch door)`,
        value:'yes', disabled:!data.components.plate36.available || !data.components.plate48.available},
    ]);
    const floor = data.components.floor;
    if (floor?.id && Number.isSafeInteger(floor.price) && floor.price > 0) {
      const label = `Add insulated floor — ${width}×${depth} — ${moneyTextPrice(floor.price)}`;
      let field = builder.querySelector('#bFloor,#f');
      if (!field) {
        add('bFloor', [
          {label:'No insulated floor', value:'', price:0},
          {label:floor.available ? label : 'Insulated floor — unavailable', value:String(floor.id), price:floor.price, disabled:!floor.available},
        ]);
        field = builder.querySelector('#bFloor');
      } else {
        const none = new Option('No insulated floor', '');
        none.dataset.p = '0';
        const option = new Option(floor.available ? label : 'Insulated floor — unavailable', String(floor.id));
        option.dataset.p = String(floor.price / 100);
        option.disabled = !floor.available;
        field.replaceChildren(none, option);
      }
      delete field.dataset.amcQuotePreference;
    }
    // Existing upgrades point to cooler components and conflicting ambient
    // ratings. Preserve these requests for quotes, not unverified freezer sales.
    const refrigeration = builder.querySelector('#bRef,#r');
    refrigeration?.querySelectorAll('option').forEach(option => {
      if (Number(option.dataset.p) > 0) {
        option.dataset.amcQuoteRequired = 'true';
        option.textContent += ' — freezer specification requires factory confirmation';
      }
    });
  }
  const moneyTextPrice = cents => new Intl.NumberFormat('en-US', {style:'currency',currency:'USD'}).format(cents / 100);
  // Some catalog templates use an empty value for an included choice. The
  // explicit placeholder marker, rather than the option value, determines
  // whether a required field is incomplete. Included options remain valid.
  const isPlaceholder = select => {
    const option = select?.selectedOptions?.[0];
    return !!option?.dataset.amcPlaceholder && !/\bincluded\b/i.test(option.textContent || '');
  };

  const moneyText = (node) => (node?.textContent || '').trim();

  const formatMoney = value => new Intl.NumberFormat('en-US', {style:'currency', currency:'USD'}).format(value);
  const ruleControl = (builder, category) => builder.querySelector(`[data-amc-rule="${category}"]`);
  const hasFloor = builder => {
    const selected = ruleControl(builder, 'floor')?.selectedOptions[0];
    return !!selected && !selected.dataset.amcPlaceholder && (selected.dataset.amcFloor === 'true' || Number(selected.dataset.p) > 0);
  };
  const hasRamp = builder => hasFloor(builder) && ruleControl(builder, 'ramp')?.value === 'external36';
  const hasStandardDoor = builder => {
    const door = ruleControl(builder, 'door');
    return !!door && !isPlaceholder(door) && (door.value === 'standard36' || /36/.test(door.selectedOptions[0]?.textContent || ''));
  };

  function normalizeOptionPriceLabels(builder) {
    builder.querySelectorAll('#bRef option,#r option').forEach(option => {
      if (option.dataset.amcPlaceholder) return;
      if (/self[- ]contained|drop[- ]in/i.test(option.textContent)) { option.remove(); return; }
      if (!/remote two-piece/i.test(option.textContent)) {
        option.textContent = option.textContent.replace(/standard two-piece system/i, 'Standard remote two-piece system');
        if (!/remote two-piece/i.test(option.textContent)) option.textContent = option.textContent.replace(/(Level\s*\d+)/i, '$1 remote two-piece');
      }
    });
    builder.querySelectorAll('select option[data-p]').forEach(option => {
      const amount = Number(option.dataset.p);
      if (!Number.isFinite(amount) || amount <= 0) return;
      // Retain the source price and wording; display its exact cents instead
      // of the rounded dollar token published by several freezer templates.
      option.textContent = option.textContent.replace(/\$\s*[\d,]+(?:\.\d+)?/, () => formatMoney(amount));
    });
  }

  function setupWalkinRules(builder) {
    function control(category, rule) {
      const row = builder.querySelector(`[data-amc-category="${category}"]`);
      let field = row.querySelector('select,input');
      if (field.tagName === 'INPUT') {
        const select = document.createElement('select');
        [...field.attributes].filter(attr => !['type','maxlength','placeholder'].includes(attr.name)).forEach(attr => select.setAttribute(attr.name, attr.value));
        field.replaceWith(select); field = select;
      }
      field.dataset.amcRule = rule;
      return field;
    }
    const floor = control('bFloor', 'floor');
    const door = control('bDoor', 'door');
    const ramp = control('bRamp', 'ramp');
    const unpricedFloor = ![...floor.options].some(option => Number(option.dataset.p) > 0);
    if (unpricedFloor) {
      // Existing freezer templates publish no floor price or component mapping.
      // Keep native values empty so their calculators cannot charge a guessed ID.
      floor.replaceChildren(new Option('No floor', ''), new Option('Request insulated floor — additional price by factory quote', ''));
      floor.options[1].dataset.amcFloor = 'true';
      floor.dataset.amcQuotePreference = 'bFloor';
    }
    floor.selectedIndex = 0;
    floor.required = false; floor.removeAttribute('aria-required');
    optionLabel(builder, floor).textContent = 'Insulated floor — optional add-on';
    let floorHelp = floor.closest('.amc-walkin-category').querySelector('small');
    if (!floorHelp) {floorHelp = document.createElement('small'); floorHelp.className = 'amc-walkin-option-help'; floor.after(floorHelp);}
    if (floorHelp) floorHelp.textContent = 'Optional floor, priced separately. Ramp not included.';
    const floorInfo = document.createElement('button');
    floorInfo.type = 'button'; floorInfo.className = 'amc-floor-info';
    floorInfo.textContent = 'i'; floorInfo.setAttribute('aria-label', 'Floor requirements and separate pricing');
    floorInfo.setAttribute('aria-expanded', 'false');
    const floorNote = document.createElement('small');
    floorNote.id = 'amc-floor-requirements'; floorNote.className = 'amc-floor-info-note'; floorNote.hidden = true;
    floorNote.textContent = 'The insulated floor is an optional, separately priced add-on, not a leveling system. An insulated floor is required when the existing floor is not level or has imperfections, and when the walk-in is installed on a second story, mezzanine or any floor above a basement, crawl space or occupied space. A solid, flat, level supporting surface is required with or without an insulated floor. Confirm upper-floor loading with a qualified professional. The external ramp is separate and requires a floor and the 36-inch standard door. Unlisted floor prices require a factory quote.';
    floorInfo.setAttribute('aria-controls', floorNote.id);
    optionLabel(builder, floor).append(floorInfo);
    floorHelp.after(floorNote);
    floorInfo.addEventListener('click', event => {
      event.preventDefault();
      floorNote.hidden = !floorNote.hidden;
      floorInfo.setAttribute('aria-expanded', String(!floorNote.hidden));
    });
    floorInfo.addEventListener('keydown', event => {
      if (event.key !== 'Escape') return;
      floorNote.hidden = true; floorInfo.setAttribute('aria-expanded', 'false');
    });
    if (door.dataset.amcQuotePreference) {
      door.replaceChildren(new Option('Factory recommendation requested', ''), new Option('36-inch standard door — quote request', 'standard36'));
      optionLabel(builder, door).textContent = 'Door size — quote preference';
    }
    // Use a non-variant value the legacy calculator does not price. The bridge
    // adds the live Shopify catalog price only to derived estimates and quote details.
    const rampProduct = liveRamp();
    const rampOption = new Option(rampProduct ? `36-inch external ramp (+${formatMoney(rampProduct.price / 100)})` : 'External ramp — confirm availability', 'external36');
    rampOption.disabled = !rampProduct;
    ramp.replaceChildren(new Option('No ramp', 'no'), rampOption);
    delete ramp.dataset.amcQuotePreference;
    ramp.required = false; ramp.removeAttribute('aria-required');
    optionLabel(builder, ramp).textContent = 'External ramp — optional add-on';
    const rampHelp = ramp.closest('.amc-walkin-category').querySelector('small');
    if (rampHelp) rampHelp.textContent = `Optional external ramp${rampProduct ? ': +' + formatMoney(rampProduct.price / 100) : ': confirm availability'}. Requires a floor and the 36-inch standard door.`;
    const message = document.createElement('small');
    message.className = 'amc-walkin-option-help'; message.dataset.amcRampDoorError = '';
    message.id = 'amc-ramp-door-requirement'; message.hidden = true;
    message.setAttribute('role', 'status'); message.setAttribute('aria-live', 'polite');
    message.textContent = 'Select the 36-inch standard door to add the external ramp.';
    ramp.after(message);
    [ramp, door].forEach(field => field.setAttribute('aria-describedby', [field.getAttribute('aria-describedby'), message.id].filter(Boolean).join(' ')));
    ramp.addEventListener('change', () => {builder.dataset.amcRampChoice = ramp.value;}, true);
  }

  function syncWalkinRules(builder) {
    const floorOn = hasFloor(builder);
    const ramp = ruleControl(builder, 'ramp');
    ramp.closest('.amc-walkin-category').hidden = !floorOn;
    const wrap = ramp.closest('#bRampWrap');
    if (wrap) wrap.style.display = floorOn ? 'block' : 'none';
    if (!floorOn) {
      ramp.value = 'no'; delete builder.dataset.amcRampChoice;
      delete ramp.dataset.amcRequiredPrepared;
      ramp.required = false; ramp.removeAttribute('aria-required'); ramp.removeAttribute('aria-invalid');
      optionLabel(builder, ramp)?.querySelector('.amc-required-mark')?.remove();
    }
    prepareRequiredSelections(builder);
    if (floorOn && builder.dataset.amcRampChoice !== undefined) ramp.value = builder.dataset.amcRampChoice;
    builder.querySelector('[data-amc-ramp-door-error]').hidden = !hasRamp(builder) || hasStandardDoor(builder);
  }

  function builderDetails(builder) {
    const selections = CATEGORIES.map(([id, name, alias]) => {
      const select = builder.querySelector(`[data-amc-category="${id}"] select`) || builder.querySelector('#' + id + (alias ? ',#' + alias : ''));
      if (select) {
        const visible = !select.closest('[hidden]') && getComputedStyle(select.closest('#bRampWrap') || select).display !== 'none';
        return `${name}${select.dataset.amcQuotePreference ? ' (unpriced quote preference)' : ''}: ${visible ? (select.selectedOptions[0]?.textContent.trim() || '') : 'Not applicable without selected floor'}`;
      }
      const preference = builder.querySelector(`[data-amc-quote-preference="${id}"]`) || builder.querySelector(`[data-amc-category="${id}"] [data-amc-rule]`);
      if (preference?.tagName === 'SELECT') return `${name}${preference.dataset.amcQuotePreference ? ' (unpriced quote preference)' : ''}: ${id === 'bRamp' && !hasFloor(builder) ? 'Not applicable without selected floor' : preference.selectedOptions[0]?.textContent.trim() || ''}`;
      return `${name} (unpriced quote preference): ${preference?.value.trim() || 'Factory recommendation requested'}`;
    });

    const details = {
      product: document.querySelector('h1')?.textContent.trim() || document.title,
      handle: location.pathname.split('/products/')[1]?.split('/')[0] || '',
      // The Shopify variant price is authoritative. Legacy embedded builders can
      // retain an older BASE constant after a catalog price update.
      base: builder.dataset.amcBase || moneyText(builder.querySelector('#bBase')) || 'Confirm with quote',
      options: builder.dataset.amcQuoteOnly ? 'Confirm with quote — model-specific options require verification' : moneyText(builder.querySelector('#bTotal, #options-price')) || new Intl.NumberFormat('en-US', {style:'currency',currency:'USD'}).format([...builder.querySelectorAll(CONTROL_SELECTOR)].reduce((sum, control) => sum + (Number(control.selectedOptions[0]?.dataset.p) || 0), 0)),
      total: builder.dataset.amcQuoteOnly ? 'Confirm with quote' : moneyText(builder.querySelector('#bGrand, #total-price, #t')), 
      selections,
      hasUnpricedPreferences: !!builder.querySelector(PREFERENCE_SELECTOR),
    };
    // One calculation supplies the visible total and the expected cart prices.
    // Do not rely on legacy handlers that omit blank-ID paid choices.
    const baseAmount = Number((details.base || '').replace(/[^0-9.-]/g, ''));
    let optionCents = [...builder.querySelectorAll(CONTROL_SELECTOR)].reduce((sum,field) => {
      if (field.closest('[hidden]') || isPlaceholder(field)) return sum;
      return sum + Math.round(Number(field.selectedOptions[0]?.dataset.p || 0) * 100);
    },0);
    if (builder.querySelector('#bPlate')?.value === 'yes') {
      const components = freezerDoorData(builder)?.components;
      optionCents += components ? (hasStandardDoor(builder) ? components.plate36.price : components.plate48.price) : (hasStandardDoor(builder) ? 42500 : 60500);
    }
    details.options = formatMoney(optionCents / 100);
    details.total = formatMoney((Math.round(baseAmount * 100) + optionCents) / 100);
    if (hasRamp(builder)) {
      if (builder.dataset.amcQuoteOnly) details.options += liveRamp() ? ' — external ramp +' + formatMoney(liveRamp().price / 100) : ' — ramp price confirmation required';
      else ['options','total'].forEach(key => {
        const amount = Number(details[key].replace(/[^0-9.-]/g, ''));
        if (Number.isFinite(amount) && liveRamp()) details[key] = formatMoney(amount + liveRamp().price / 100);
      });
    }
    return details;
  }

  function formattedDetails(details) {
    return [
      `Walk-in configuration: ${details.product}`,
      ...details.selections.map((selection) => `• ${selection}`),
      `Base unit: ${details.base}`,
      `Selected options: ${details.options}`,
      `Configured planning estimate: ${details.total}`,
      ...(details.hasUnpricedPreferences ? ['Planning estimate excludes unpriced requested preferences. Compatibility, availability and additional charges require factory confirmation.'] : []),
      'Please confirm final specifications, current pricing, freight, and estimated lead time.',
    ].join('\n');
  }

  function quoteUrl(details) {
    const url = new URL('/pages/request-quote', location.origin);
    url.searchParams.set('source', 'walk-in-configurator');
    url.searchParams.set('product', details.handle);
    url.searchParams.set('configuration', formattedDetails(details));
    return `${url.pathname}${url.search}`;
  }

  function syncMainPlanningPrice(builder, details, valid) {
    const productInfo = builder.closest('product-info');
    const purchaseColumn = productInfo?.querySelector('.product__info-wrapper');
    const host = purchaseColumn?.querySelector('[id^="price-"]');
    if (!host) return;
    host.classList.add('amc-configured-price-host');
    let panel = purchaseColumn.querySelector('.amc-configured-main-price');
    if (!panel) {
      panel = document.createElement('div');
      panel.className = 'amc-configured-main-price';
      panel.innerHTML = '<span class="amc-configured-main-price__label">Configured price</span><strong data-amc-main-planning-price></strong><small data-amc-main-base-price></small><small data-amc-main-price-note></small>';
      host.before(panel);
    }
    panel.querySelector('[data-amc-main-planning-price]').textContent = details.total || 'Confirm with quote';
    const nativePrice = host.querySelector('.price--on-sale .price-item--sale') || host.querySelector('.price-item--regular');
    const base = details.base === 'Confirm with quote' ? moneyText(nativePrice) : details.base;
    panel.querySelector('[data-amc-main-base-price]').textContent = builder.dataset.amcQuoteOnly
      ? `Base unit: ${base || 'Confirm with quote'}. Configuration priced by quote.`
      : `Base unit: ${base} · Selected priced options included above.`;
    panel.querySelector('[data-amc-main-price-note]').textContent = builder.dataset.amcQuoteOnly
      ? 'Final configuration and itemized pricing require confirmation.'
      : `${valid ? 'Ready to add your selected configuration.' : 'Choose all required options to add to cart.'} Shipping and tax calculated separately.${details.hasUnpricedPreferences ? ' Unpriced requests require a quote.' : ''}`;
    const installmentTerms = purchaseColumn.querySelector('.amc-shop-pay-terms');
    if (installmentTerms && panel.nextElementSibling !== installmentTerms) panel.after(installmentTerms);
    // The theme replaces the native price container's inner HTML on variant
    // updates. Our sibling stays intact; this observer restores presentation
    // and refreshed base context without altering native prices or cart data.
    if (!host.dataset.amcPlanningObserved) {
      host.dataset.amcPlanningObserved = 'true';
      const observer = new MutationObserver(() => {
        if (builder.isConnected) syncMainPlanningPrice(builder, builderDetails(builder), validateRequiredSelections(builder, false));
        else observer.disconnect();
      });
      observer.observe(host, {childList:true, subtree:true, characterData:true});
    }
  }

  function updateQuoteState(builder) {
    const details = builderDetails(builder);
    builder.querySelectorAll('[data-amc-estimate-field]').forEach(node => {node.textContent = details[node.dataset.amcEstimateField];});
    const url = quoteUrl(details);
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(details)); } catch (_) {}

    const optionAmount = Number((details.options || '').replace(/[^0-9.-]/g, '')) || 0;
    const summary = builder.querySelector('[data-amc-config-summary]');
    if (summary) {
      summary.textContent = optionAmount > 0
        ? 'Configured total includes selected priced options.'
        : 'Standard configuration — complete the required choices above.';
    }
    const valid = validateRequiredSelections(builder, false);
    syncMainPlanningPrice(builder, details, valid);
    return { details, url, valid };
  }

  function optionLabel(builder, select) {
    return [...builder.querySelectorAll('label')].find((candidate) => candidate.htmlFor === select.id);
  }

  function requiredSelects(builder) {
    return [...builder.querySelectorAll(CONTROL_SELECTOR + ',select[data-amc-rule="ramp"]')].filter((select) => {
      const label = optionLabel(builder, select);
      const conditionalContainer = select.closest('[hidden], #bRampWrap');
      const visible = !select.closest('[hidden]') && getComputedStyle(conditionalContainer || select).display !== 'none';
      return visible && REQUIRED_OPTION_IDS.has(select.id);
    });
  }

  function prepareRequiredSelections(builder) {
    const ramp = builder.querySelector('#bRamp');
    if (ramp && getComputedStyle(ramp.closest('#bRampWrap') || ramp).display === 'none') delete ramp.dataset.amcRequiredPrepared;
    requiredSelects(builder).forEach((select) => {
      const label = optionLabel(builder, select);
      const name = (label?.textContent || 'option').replace(/\s+/g, ' ').replace(/\*|required/gi, '').trim();
      select.required = true;
      select.setAttribute('aria-required', 'true');
      if (label && !label.querySelector('.amc-required-mark')) {
        label.insertAdjacentHTML('beforeend', ' <span class="amc-required-mark">Required</span>');
      }
      if (![...select.options].some((option) => option.dataset.amcPlaceholder)) {
        const placeholder = new Option(`Select ${name.toLowerCase()}`, REQUIRED_PLACEHOLDER_VALUE, true, true);
        placeholder.disabled = true;
        placeholder.dataset.amcPlaceholder = 'true';
        select.add(placeholder, 0);
      }
      if (!select.dataset.amcRequiredPrepared) {
        select.selectedIndex = [...select.options].findIndex(option => option.dataset.amcPlaceholder);
        select.dataset.amcRequiredPrepared = 'true';
      }
      if (/hinge/i.test(name) && label && !label.querySelector('.amc-hinge-help')) {
        const help = document.createElement('span');
        help.className = 'amc-hinge-help';
        help.setAttribute('role', 'note');
        help.setAttribute('tabindex', '0');
        help.setAttribute('aria-label', 'Door hinge selection cannot be changed after the order is placed. Confirm the opening direction before submitting your configuration.');
        help.setAttribute('data-tooltip', 'Door hinge selection cannot be changed after the order is placed. Confirm the opening direction before submitting your configuration.');
        help.textContent = 'i';
        label.append(' ', help);
      }
    });
  }

  function clearRequiredSelections(builder) {
    requiredSelects(builder).forEach((select) => {
      select.selectedIndex = [...select.options].findIndex(option => option.dataset.amcPlaceholder);
    });
  }

  function validateRequiredSelections(builder, announce = true) {
    const missing = requiredSelects(builder).filter((select) => isPlaceholder(select));
    if (announce) builder.dataset.amcValidationAttempted = 'true';
    requiredSelects(builder).forEach(select => {
      if (builder.dataset.amcValidationAttempted) select.setAttribute('aria-invalid', String(isPlaceholder(select)));
      else select.removeAttribute('aria-invalid');
    });
    const incompatibleRamp = hasRamp(builder) && !hasStandardDoor(builder);
    const door = ruleControl(builder, 'door');
    if (door) {
      if (incompatibleRamp) door.setAttribute('aria-invalid', 'true');
      else if (!requiredSelects(builder).includes(door)) door.removeAttribute('aria-invalid');
    }
    const error = builder.querySelector('[data-amc-required-error]');
    if (error) {
      error.textContent = incompatibleRamp ? 'Select the 36-inch standard door to add the external ramp.' : 'Select every required configuration option before adding to cart.';
      error.hidden = (missing.length === 0 && !incompatibleRamp) || !builder.dataset.amcValidationAttempted;
      if (announce && missing.length) error.focus({ preventScroll: true });
    }
    return missing.length === 0 && !incompatibleRamp;
  }

  function syncAnnouncement(builder) {
    const message = builder.querySelector('[data-amc-configuration-status]');
    if (!message) return;
    const details = builderDetails(builder);
    message.textContent = `Configuration updated: ${details.total}. Selected options ${details.options}.`;
  }

  function organizeCategories(builder, intro) {
    const group = document.createElement('div');
    group.className = 'amc-walkin-categories';
    intro.after(group);
    const oldParents = new Set();
    CATEGORIES.forEach(([id, name, alias]) => {
      const row = document.createElement('div');
      row.className = 'amc-walkin-category';
      row.dataset.amcCategory = id;
      const select = builder.querySelector('#' + id + (alias ? ',#' + alias : ''));
      if (select) {
        oldParents.add(select.parentElement);
        const label = optionLabel(builder, select);
        const help = builder.querySelector('#amc-option-help-' + select.id);
        row.append(label);
        if (id === 'bRamp' && select.closest('#bRampWrap')) {
          // Preserve the native dependency container and listeners while keeping
          // the category heading visible even before a floor is selected.
          row.append(select.closest('#bRampWrap'));
          const note = document.createElement('small');
          note.className = 'amc-walkin-option-help';
          note.dataset.amcRampHint = '';
          note.textContent = 'Ramp selection becomes available when an eligible floor is selected.';
          row.append(note);
        } else row.append(select);
        if (help) row.append(help);
      } else {
        const input = document.createElement('input');
        input.type = 'text';
        input.id = 'amc-preference-' + id;
        input.dataset.amcQuotePreference = id;
        input.className = 'amc-walkin-option amc-walkin-preference';
        input.maxLength = 160;
        input.placeholder = 'Describe preference or request factory recommendation';
        const label = document.createElement('label');
        label.htmlFor = input.id;
        label.className = 'amc-walkin-option-label';
        label.textContent = name + ' — quote preference';
        const help = document.createElement('small');
        help.id = input.id + '-help';
        help.className = 'amc-walkin-option-help';
        help.textContent = 'Optional, unpriced request. Leave blank for a factory recommendation. Compatibility and availability require confirmation.';
        input.setAttribute('aria-describedby', help.id);
        row.append(label, input, help);
      }
      group.append(row);
    });
    // Remove obsolete decorative category headings left by the legacy freezer
    // containers; their descriptive labels are retained with the actual field.
    oldParents.forEach(parent => {
      if (parent === builder || parent.id === 'bRampWrap' || group.contains(parent)) return;
      if (!parent.querySelector('select,input,button,#bBase,#bTotal,#bGrand,#options-price,#total-price,#t')) parent.hidden = true;
    });
  }

  function discoverBuilder() {
    // Legacy short IDs are only meaningful inside a confirmed full-unit walk-in PDP.
    const title = document.querySelector('h1')?.textContent || '';
    const walkInStyle = document.querySelector('product-info[data-amc-walkin-product="true"]');
    if (!/walk[\s-]?in/i.test(title + ' ' + location.pathname) && !walkInStyle) return null;
    const description = document.querySelector('product-info .product__description, .product__description');
    if (!description) return null;
    const controls = [...description.querySelectorAll(CONTROL_SELECTOR)];
    const special = location.pathname.split('/').pop() === 'double-door-mortuary-cooler-amc-model-3x3-s';
    const action = description.querySelector('#bAdd, #a');
    const total = description.querySelector(special ? '#bTotal' : '#bGrand, #total-price, #t');
    if ((!action && !special) || !total || !controls.some(control => ['bRef','r'].includes(control.id))) return null;
    let builder = (action || total).parentElement;
    while (builder && builder !== description.parentElement) {
      if (builder.contains(total) && controls.every(control => builder.contains(control))) return {builder, action, controls, special};
      builder = builder.parentElement;
    }
    return null;
  }

  function trailingTechnicalNodes(builder) {
    const reset = builder.querySelector('#bReset');
    if (!reset || reset.parentElement !== builder) return [];
    const nodes = [];
    for (let node = reset.nextSibling; node; node = node.nextSibling) {
      if (node.nodeType === Node.TEXT_NODE) {
        if (node.textContent.trim()) nodes.push(node);
      } else if (node.nodeType === Node.ELEMENT_NODE &&
        !node.matches('script,style,template') &&
        !node.matches('input,select,button,form') &&
        !node.querySelector('input,select,button,form')) nodes.push(node);
    }
    return nodes;
  }

  function positionPurchaseBuilder(builder, technicalNodes) {
    const productInfo = builder.closest('product-info');
    if (!productInfo) return;
    const purchaseColumn = productInfo.querySelector('.product__info-wrapper');
    const form = purchaseColumn?.querySelector('product-form');
    // Keep the source layout if the primary native purchase target is absent.
    // Moving the existing node preserves every option, listener and total node.
    if (!form || builder.contains(form) || form.contains(builder)) return;
    const preserved = technicalNodes.filter(node => node.parentNode === builder);
    if (preserved.length) {
      // Leave technical copy at its original full-width description position.
      // Only the interactive builder belongs beside the price and purchase form.
      const content = document.createElement('section');
      content.className = 'amc-walkin-full-specifications rte';
      content.setAttribute('aria-label', 'Specifications, installation and warranty');
      preserved.forEach(node => content.append(node));
      builder.after(content);
    }
    form.before(builder);
    builder.classList.add('amc-walkin-purchase-builder');
    purchaseColumn.classList.add('amc-walkin-purchase-column');
  }


  function cartConfiguration(builder) {
    if (quoteOnlySelection(builder)) throw new Error('The selected freezer floor or refrigeration upgrade needs factory confirmation. Use the configuration quote link; no items were added.');
    const info = builder.closest('product-info');
    const form = info.querySelector('product-form form[action*="/cart/add"]');
    const id = Number(form?.querySelector('[name="id"]')?.value);
    if (!Number.isSafeInteger(id) || id <= 0) throw new Error('The cooler is unavailable for online ordering.');
    const quantity = Number(form.querySelector('[name="quantity"]')?.value || info.querySelector('input[name="quantity"]')?.value || 1);
    if (!Number.isSafeInteger(quantity) || quantity < 1) throw new Error('Enter a whole-number quantity of at least 1.');
    const unpricedText = [...builder.querySelectorAll('input[data-amc-quote-preference]')].find(input=>input.value.trim());
    if (unpricedText) throw new Error('Custom configuration requests need pricing before online ordering. Clear the request or use the optional quote link.');
    const details = builderDetails(builder);
    const cents = text => Math.round(Number(String(text).replace(/[^0-9.-]/g, '')) * 100);
    const items = [{id, quantity, expectedPrice: cents(details.base), label: 'Cooler base unit'}];
    const properties = {
      'Cooler configuration': details.product,
      'Included safety handle': 'Interior emergency-release handle',
      ...(builder.querySelector('#bFloor') && !hasFloor(builder) ? {'Floorless terms accepted': 'Solid, flat, level slab with no imperfections; ground-level install, not on a second story or above a basement, crawl space or occupied space'} : {}),
      'Included panel warranty': '15-year panel warranty; warranty terms apply',
      'Included compressor warranty': '5-year compressor parts warranty; parts only; warranty terms apply',
      'Refrigeration system': 'Remote two-piece system'
    };
    CATEGORIES.forEach(([category, name]) => {
      const field = builder.querySelector(`[data-amc-category="${category}"] select`);
      if (!field || field.closest('[hidden]')) return;
      const option = field.selectedOptions[0];
      if (!option || isPlaceholder(field)) return;
      properties[name] = option.textContent.trim();
      if (field.dataset.amcQuotePreference && (field.selectedIndex > 0 || Number(option.dataset.p) > 0)) {
        throw new Error(`${name} is not priced for online ordering. Remove that request or use the optional quote link.`);
      }
      const price = Number(option.dataset.p || 0);
      if (price > 0) {
        // Some regenerated configurator descriptions keep the Shopify variant
        // in data-variant-id while their visible value is a legacy short code.
        // Prefer the explicit mapped ID, then fall back to the option value.
        let variant = Number(option.dataset.variantId || option.value);
        // Exact same published 48-inch door upgrade, verified in the catalog.
        if (variant === 52289694990638) variant = 52289425441070;
        if (!Number.isSafeInteger(variant) || variant <= 0) throw new Error(`${name} has no online ordering option. Request a quote for this selection.`);
        items.push({id:variant, quantity, expectedPrice:Math.round(price*100), label:name});
      }
    });
    const plate = builder.querySelector('#bPlate');
    if (plate?.value === 'yes') {
      const standard = hasStandardDoor(builder);
      const component = freezerDoorData(builder)?.components[standard ? 'plate36' : 'plate48'];
      if (component) properties['Diamond plate'] = `${standard ? '36' : '48'}-inch door diamond plate — ${formatMoney(component.price / 100)}`;
      items.push({id:component?.id || (standard?52289425473838:52289425506606),quantity,expectedPrice:component?.price ?? (standard?42500:60500),label:'Diamond plate'});
    }
    if (hasRamp(builder)) {
      const ramp = liveRamp();
      if (!ramp) throw new Error('Confirm ramp price and availability before ordering.');
      items.push({id:ramp.id,quantity,expectedPrice:ramp.price,label:'36-inch external ramp'});
    }
    if (items.reduce((sum,item)=>sum+item.expectedPrice,0) !== cents(details.total)) {
      throw new Error('The selected price needs confirmation. No items have been added; review your options or request a quote.');
    }
    return {items, properties};
  }

  function setupConfiguredCart(builder, quoteAction) {
    const info = builder.closest('product-info');
    const section = info.closest('.section,.shopify-section') || info.parentElement;
    // Only the main product form belongs to this configured build. Related-product
    // quick-add forms can be nested inside product-info and must retain their own labels.
    const nativeButtons = () => [...info.querySelectorAll('product-form .product-form__submit[data-amc-awaiting-configurator="true"]')];
    const configuredForm = () => info.querySelector('product-form.amc-product-form--configured-hidden form[action*="/cart/add"]');
    const baseUnavailable = nativeButtons().some(control=>(control.disabled && control.dataset.amcAwaitingConfigurator!=='true') || /sold out|unavailable/i.test(control.textContent));
    const button = document.createElement('button');
    button.type = 'button'; button.className = 'amc-walkin-cart';
    const label = 'Add entire build to cart';
    button.textContent = baseUnavailable ? 'Unavailable' : label;
    const guidance = document.createElement('p');
    guidance.className = 'amc-walkin-cart-guidance'; guidance.id = 'amc-walkin-cart-guidance';
    guidance.setAttribute('role','status'); guidance.setAttribute('aria-live','polite');
    const message = document.createElement('p');
    message.className = 'amc-walkin-cart-message'; message.hidden = true; message.setAttribute('role','alert'); message.tabIndex = -1;
    quoteAction.before(guidance,button,message);
    let busy = false, phase = '', observer;
    const watch = () => observer.observe(section,{subtree:true,childList:true,characterData:true,attributes:true,attributeFilter:['disabled','type']});
    const sync = () => {
      observer?.disconnect();
      const valid = validateRequiredSelections(builder,false);
      const requiresQuote = quoteOnlySelection(builder);
      const required = requiredSelects(builder);
      const missing = required.filter(field=>isPlaceholder(field)).length;
      guidance.textContent = !valid
        ? (missing ? `Select ${missing} required ${missing===1?'option':'options'} before adding your build.` : 'The external ramp requires a floor and the 36-inch standard door.')
        : requiresQuote ? 'This floor or freezer refrigeration selection requires factory confirmation. Request an itemized quote below.'
        : 'Required selections complete. Review optional extras, then add your entire build.';
      const all = [button,...nativeButtons()];
      all.forEach(control=>{
        if (!control.dataset.amcConfiguredSubmit) {
          control.dataset.amcCatalogUnavailable = String(baseUnavailable || (control!==button && /sold out|unavailable/i.test(control.textContent)));
          control.dataset.amcConfiguredSubmit = 'true';
        }
        control.disabled = busy || !valid || requiresQuote || control.dataset.amcCatalogUnavailable==='true';
        control.setAttribute('aria-disabled',String(control.disabled));
        control.setAttribute('aria-describedby',guidance.id);
        const text = busy ? phase : label;
        const span = [...control.querySelectorAll('span')].find(node=>/add|build|cart|quote|required|checking|review|unavailable|sold out/i.test(node.textContent) && !node.closest('.loading__spinner'));
        if (control.dataset.amcCatalogUnavailable!=='true') {
          if (span) {if(span.textContent!==text) span.textContent=text;}
          else if(control.textContent!==text) control.textContent=text;
        }
      });
      info.querySelectorAll('.shopify-payment-button,shopify-accelerated-checkout').forEach(node=>{node.hidden=true;node.style.setProperty('display','none','important');});
      watch();
    };
    const purchase = async event => {
      event?.preventDefault(); event?.stopImmediatePropagation();
      if (busy) return;
      if (!validateRequiredSelections(builder,true)) {sync();return;}
      if (nativeButtons().some(control=>control.dataset.amcCatalogUnavailable==='true')) return;
      message.hidden=true;
      busy=true;phase='Checking configuration…';sync();
      try {
        const configuration=cartConfiguration(builder);
        configuration.properties['_AMC configuration']=(window.crypto?.randomUUID?.() || String(Date.now()));
        await window.AMCWalkinCart.add({...configuration,onStatus:text=>{phase=text;sync();}});
        phase='Added — opening cart…';sync();
        location.assign((window.Shopify?.routes?.root || '/')+'cart');
      } catch(error) {
        message.textContent=error.message || 'Could not add this configuration. Please review your cart before trying again.';
        message.hidden=false;message.focus({preventScroll:true});
        if(error.cartReviewRequired) {
          const review=document.createElement('a');review.href=(window.Shopify?.routes?.root || '/')+'cart';review.textContent=' Review cart';message.append(review);
          phase='Review cart before adding again';
        } else {busy=false;phase='';}
        sync();
      }
    };
    // The native product form also delegates here; it must never submit only the base variant.
    info.amcAddConfiguredBuild = purchase;
    info.querySelectorAll('[data-amc-configurator-hold]').forEach(notice => {notice.hidden=true;notice.style.setProperty('display','none','important');});
    // Capture before native listeners, keeping type=submit for lazy product-form
    // initialization and supporting replacement buttons without duplicate binds.
    window.addEventListener('click',event=>{
      const target=event.target.closest?.('button');
      if(target && (target===button || nativeButtons().includes(target))) purchase(event);
    },true);
    window.addEventListener('submit',event=>{
      if(event.target===configuredForm()) purchase(event);
    },true);
    window.addEventListener('keydown',event=>{
      if(event.key==='Enter' && event.target.closest?.('product-form.amc-product-form--configured-hidden')?.closest('product-info')===info && !event.target.matches('textarea,a,button')) purchase(event);
    },true);
    observer=new MutationObserver(records=>{
      if(!info.isConnected) {observer.disconnect();return;}
      if(records.some(record=>record.target===button || record.target.closest?.('product-form') || [...record.addedNodes].some(node=>node.nodeType===1 && (node.matches('product-form') || node.querySelector('product-form'))))) sync();
    });
    builder.addEventListener('amc:configuration-updated',sync);
    builder.dataset.amcConfiguredCart='true';
    info.classList.add('amc-configured-cart-ready');
    sync();
  }

  function enhanceBuilder() {
    const found = discoverBuilder();
    if (!found) return;
    const {builder, controls, special} = found;
    // Retire the oversized legacy support CTA embedded in older product HTML.
    // Contact and RFQ paths remain available in the compact buy box and the
    // support section below the specifications.
    builder.closest('product-info')?.querySelectorAll('.product__description div').forEach((panel) => {
      const firstParagraph = panel.querySelector(':scope > p:first-child');
      if (firstParagraph?.textContent.trim().toLowerCase() === 'ready to order?') panel.remove();
    });
    const technicalNodes = trailingTechnicalNodes(builder);
    let {action} = found;
    if (!action) {action = document.createElement('button');action.type = 'button';action.id = 'bAdd';builder.append(action);}
    if (builder.dataset.amcBridgeReady) return;
    builder.dataset.amcBridgeReady = 'true';
    builder.classList.add('amc-walkin-configurator', 'amc-walkin-configurator--uniform');
    builder.id ||= 'amc-walkin-options';
    if (special) builder.dataset.amcSpecialCart = 'true';
    builder.closest('product-info')?.classList.add('amc-walkin-product');
    // Trigger the existing calculator before adding prompts: short freezer templates
    // initially round the printed base amount, but their handlers retain exact cents.
    controls[0].dispatchEvent(new Event('change', {bubbles:true}));
    const amount = moneyText(builder.querySelector('#bGrand, #total-price, #t'));
    const options = controls.reduce((sum, control) => sum + (Number(control.selectedOptions[0]?.dataset.p) || 0), 0);
    const numeric = Number(amount.replace(/[^0-9.-]/g, ''));
    if (Number.isFinite(numeric) && amount) builder.dataset.amcBase = new Intl.NumberFormat('en-US',{style:'currency',currency:'USD'}).format(numeric - options);

    // Always prefer the live Shopify variant price over a price hard-coded in
    // the product description. This keeps every configured total and cart-price
    // verification synchronized after catalog price changes.
    const nativePriceHost = builder.closest('product-info')?.querySelector('[id^="price-"]');
    const native = nativePriceHost?.querySelector('.price--on-sale .price-item--sale') || nativePriceHost?.querySelector('.price-item--regular');
    const nativeAmount = moneyText(native);
    if (nativeAmount) builder.dataset.amcBase = nativeAmount;
    prepareFreezerDoors(builder, controls);
    const freezerData = freezerDoorData(builder);
    if (freezerData) builder.dataset.amcBase = formatMoney(freezerData.basePrice / 100);
    [...builder.querySelectorAll(':scope > p')].filter(node => /build it out|configure your exact cooler/i.test(node.textContent)).forEach(node => {node.hidden = true;});
    const heading = document.createElement('h2');
    heading.className = 'amc-walkin-configurator__heading';
    heading.textContent = 'Walk-in configuration';
    builder.prepend(heading);
    const intro = document.createElement('p');
    intro.className = 'amc-walkin-configurator__intro';
    intro.textContent = 'Choose your required options and optional extras.';
    heading.after(intro);
    controls.forEach(select => {
      let label = optionLabel(builder, select);
      if (!label && select.previousElementSibling?.matches('label')) {label = select.previousElementSibling; label.htmlFor = select.id;}
      if (!label) {
        label = document.createElement('label');
        label.htmlFor = select.id;
        label.textContent = LABELS[select.id];
        select.before(label);
      }
      const originalLabel = label.textContent.trim();
      label.textContent = LABELS[select.id];
      if (originalLabel && originalLabel.toLowerCase() !== LABELS[select.id].toLowerCase()) {
        const help = document.createElement('small');
        help.className = 'amc-walkin-option-help'; help.id = 'amc-option-help-' + select.id; help.textContent = originalLabel;
        select.after(help);
        select.setAttribute('aria-describedby', [select.getAttribute('aria-describedby'),help.id].filter(Boolean).join(' '));
      }
      select.classList.add('amc-walkin-option');
      label.classList.add('amc-walkin-option-label');
    });

    normalizeOptionPriceLabels(builder);
    organizeCategories(builder, intro);
    setupWalkinRules(builder);
    syncWalkinRules(builder);
    // Resolve exact existing IDs; never infer floor fit from nominal area.
    const floorOptions = [...(ruleControl(builder, 'floor')?.options || [])].filter(o => Number(o.dataset.p) > 0 && /^\d{10,}$/.test(o.value));
    if (floorOptions.length) {
      builder.dataset.amcFloorPricesPending = 'true';
      Promise.all(floorOptions.map(async option => {
        try {
          let currentFloor = (liveCatalog.floors || []).find(v => String(v.id) === option.value);
          if (!currentFloor) {
            const response = await fetch(`/variants/${option.value}.js`, {credentials:'same-origin', headers:{Accept:'application/json'}});
            if (!response.ok) throw new Error('Floor unavailable');
            currentFloor = await response.json();
          }
          if (!Number.isSafeInteger(currentFloor.price) || currentFloor.price <= 0 || !currentFloor.available) throw new Error('Floor price unavailable');
          option.dataset.p = String(currentFloor.price / 100);
          option.dataset.amcFloor = 'true';
          option.textContent = option.textContent.replace(/\$\s*[\d,]+(?:\.\d+)?/, () => formatMoney(currentFloor.price / 100));
        } catch (_) {
          option.dataset.amcPriceUnverified = 'true';
          option.disabled = true;
          option.textContent = 'Insulated floor — price confirmation required';
        }
      })).finally(() => {
        delete builder.dataset.amcFloorPricesPending;
        ruleControl(builder, 'floor').dispatchEvent(new Event('change', {bubbles:true}));
      });
    }

    const legacyMessage = builder.querySelector('#bMsg');
    if (legacyMessage) {
      legacyMessage.removeAttribute('role');
      legacyMessage.removeAttribute('aria-live');
      legacyMessage.setAttribute('aria-hidden', 'true');
      legacyMessage.hidden = true;
      legacyMessage.style.setProperty('display', 'none', 'important');
    }

    /* Keep the remote builder's held cart control and handler untouched. A
       separate quote link prevents accidental/partial add-on cart requests. */
    action.hidden = true;
    action.setAttribute('aria-hidden', 'true');
    action.setAttribute('tabindex', '-1');
    action.addEventListener('click', event => {event.preventDefault(); event.stopImmediatePropagation();}, true);
    const quoteAction = document.createElement('a');
    quoteAction.className = 'amc-walkin-configurator__quote';
    quoteAction.textContent = 'Prefer an AMC quote?';
    quoteAction.setAttribute('tabindex', '0');
    quoteAction.setAttribute('role', 'link');
    action.insertAdjacentElement('afterend', quoteAction);
    const estimate = document.createElement('dl');
    estimate.className = 'amc-walkin-estimate';
    estimate.innerHTML = '<div><dt>Base unit</dt><dd data-amc-estimate-field="base"></dd></div><div><dt>Selected options</dt><dd data-amc-estimate-field="options"></dd></div><div><dt>Configured price</dt><dd data-amc-estimate-field="total"></dd></div>';
    quoteAction.before(estimate);
    if (builder.querySelector(PREFERENCE_SELECTOR)) {
      const note = document.createElement('p');
      note.className = 'amc-walkin-unpriced-note';
      note.textContent = 'Unpriced requests are excluded. Compatibility, availability and additional charges are confirmed by quote.';
      estimate.after(note);
    }
    builder.querySelectorAll('#bBase,#bTotal,#bGrand,#options-price,#total-price,#t').forEach(node => {
      let panel = node;
      while (panel.parentElement !== builder && !panel.parentElement.querySelector(CONTROL_SELECTOR)) panel = panel.parentElement;
      if (!panel.querySelector(CONTROL_SELECTOR)) panel.hidden = true;
    });
    const status = document.createElement('div');
    status.className = 'amc-walkin-configurator__status';
    status.setAttribute('data-amc-configuration-status', '');
    quoteAction.insertAdjacentElement('afterend', status);
    const requiredError = document.createElement('p');
    requiredError.className = 'amc-walkin-configurator__required-error';
    requiredError.setAttribute('data-amc-required-error', '');
    requiredError.setAttribute('role', 'alert');
    requiredError.setAttribute('tabindex', '-1');
    requiredError.hidden = true;
    requiredError.textContent = 'Select every required configuration option before adding to cart.';
    quoteAction.insertAdjacentElement('beforebegin', requiredError);
    prepareRequiredSelections(builder);

    const syncQuoteAction = () => {
      syncWalkinRules(builder);
      const state = updateQuoteState(builder);
      if (state.valid) {
        quoteAction.href = state.url;
        quoteAction.removeAttribute('aria-disabled');
      } else {
        quoteAction.removeAttribute('href');
        quoteAction.setAttribute('aria-disabled', 'true');
      }
      syncAnnouncement(builder);
      builder.dispatchEvent(new Event('amc:configuration-updated'));
    };

    builder.querySelectorAll(PREFERENCE_SELECTOR + ',select[data-amc-rule="ramp"]').forEach(input => {
      input.addEventListener('input', syncQuoteAction);
      input.addEventListener('change', syncQuoteAction);
    });
    builder.querySelectorAll(CONTROL_SELECTOR).forEach((select) => {
      select.addEventListener('change', () => setTimeout(() => {
        syncQuoteAction();
      }, 0));
    });
    if (!builder.querySelector('#bReset')) {
      const reset = document.createElement('button');
      reset.type = 'button'; reset.id = 'bReset'; reset.textContent = 'Reset options';
      reset.addEventListener('click', () => {
        controls.forEach(control => {control.selectedIndex = [...control.options].findIndex(option => !option.dataset.amcPlaceholder);});
        controls[0].dispatchEvent(new Event('change', {bubbles:true}));
      });
      quoteAction.after(reset);
    }
    builder.querySelector('#bReset')?.addEventListener('click', () => {
      delete builder.dataset.amcRampChoice;
      builder.querySelectorAll(PREFERENCE_SELECTOR).forEach(input => {if (input.tagName === 'SELECT') input.selectedIndex = 0; else input.value = '';});
      ruleControl(builder, 'floor').selectedIndex = 0;
      setTimeout(() => {
        prepareRequiredSelections(builder);
        clearRequiredSelections(builder);
        controls[0].dispatchEvent(new Event('change', {bubbles:true}));
        syncQuoteAction();
      }, 0);
      setTimeout(() => {
        prepareRequiredSelections(builder);
        clearRequiredSelections(builder);
        controls[0].dispatchEvent(new Event('change', {bubbles:true}));
        syncQuoteAction();
      }, 100);
    });

    setupConfiguredCart(builder, quoteAction);
    document.querySelectorAll('.amc-walkin-base-notice a').forEach(link => link.setAttribute('href', '#' + builder.id));
    quoteAction.addEventListener('keydown', event => { if (event.key === 'Enter' && !validateRequiredSelections(builder, true)) event.preventDefault(); });
    quoteAction.addEventListener('click', (event) => {
      syncQuoteAction();
      if (validateRequiredSelections(builder, true)) return;
      event.preventDefault();
    });
    syncQuoteAction();
    syncAnnouncement(builder);
    // Initial prompts must also update the original calculator/floor dependency.
    controls[0].dispatchEvent(new Event('change', {bubbles:true}));
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('aria-atomic', 'true');
    positionPurchaseBuilder(builder, technicalNodes);
  }

  function prefillQuote() {
    const params = new URLSearchParams(location.search);
    if (params.get('source') !== 'walk-in-configurator') return;
    let text = params.get('configuration') || '';
    if (!text) {
      try {
        const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
        if (stored) text = formattedDetails(stored);
      } catch (_) {}
    }
    if (!text) return;

    const fill = () => {
      const textarea = document.querySelector('#AmeRfqBody, main textarea[name="contact[body]"]');
      if (!textarea || textarea.value.trim()) return false;
      textarea.value = text;
      textarea.dispatchEvent(new Event('input', { bubbles: true }));
      textarea.dispatchEvent(new Event('change', { bubbles: true }));
      textarea.closest('.field, .contact-field')?.classList.add('amc-prefilled-configuration');
      return true;
    };
    if (!fill()) {
      const observer = new MutationObserver(() => { if (fill()) observer.disconnect(); });
      observer.observe(document.documentElement, { childList: true, subtree: true });
      setTimeout(() => observer.disconnect(), 8000);
    }
  }

  function positionCompletionEquipment() {
    const productInfo = document.querySelector('product-info.amc-walkin-product');
    const details = productInfo?.querySelector('.product > .product__media-submedia-wrapper');
    const section = document.querySelector('.section-related-products:has(.amc-curated-products)');
    if (!details || !section || section.contains(details)) return;
    // Move the original cards and controls intact, ahead of the long description.
    section.classList.add('amc-walkin-completion');
    if (section.nextElementSibling !== details) details.before(section);
  }

  // Read-only helpers so the quote list can carry the exact configured build.
  window.AMCWalkinBridge = Object.freeze({
    details: builder => builderDetails(builder),
    configuration: builder => cartConfiguration(builder),
    valid: (builder, show) => validateRequiredSelections(builder, !!show),
    text: details => formattedDetails(details)
  });

  function init() {
    enhanceBuilder();
    positionCompletionEquipment();
    prefillQuote();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
})();
